import React, { Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Star, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { clients } from '../lib/data';
import { Button } from '../components/ui/button';
const clientSentiment: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
}> = {
  etoro: {
    positive: 52,
    neutral: 38,
    negative: 22
  },
  mastercard: {
    positive: 41,
    neutral: 35,
    negative: 13
  },
  ibm: {
    positive: 68,
    neutral: 55,
    negative: 33
  }
};
export function ClientSelection() {
  const navigate = useNavigate();
  return <div className="min-h-screen flex flex-col" style={{
    backgroundColor: '#010827'
  }} data-id="element-660">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-4" data-id="element-661">
        <div className="flex flex-col leading-tight" data-id="element-662">
          <span className="font-logo text-[24px] tracking-wide" style={{
          color: '#FFFFFF'
        }} data-id="element-663">
            Esclapon
          </span>
          <span className="font-logo text-[24px] tracking-wide" style={{
          color: '#FFFFFF'
        }} data-id="element-664">
            & Co.
          </span>
        </div>
        <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-10 px-4 text-sm gap-2" data-id="element-665">
          <LogOut className="h-4 w-4" data-id="element-666" />
          Esci
        </Button>
      </div>

      {/* Centered Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 -mt-16" data-id="element-667">
        <motion.h1 initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-2xl md:text-3xl font-normal tracking-tight text-white/80 mb-10" data-id="element-668">
          Seleziona un cliente
        </motion.h1>

        <motion.div initial="hidden" animate="visible" variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1
          }
        }
      }} className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full max-w-4xl" data-id="element-669">
          {clients.map(client => {
          const sentiment = clientSentiment[client.id] || {
            positive: 0,
            neutral: 0,
            negative: 0
          };
          return <motion.button key={client.id} variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} onClick={() => {
            localStorage.setItem('selectedClientId', client.id);
            window.dispatchEvent(new Event('client-changed'));
            navigate('/home');
          }} className="w-full flex flex-col rounded-2xl bg-white border border-border hover:border-[#0187c3]/50 hover:shadow-[0_0_30px_rgba(1,135,195,0.12)] transition-all duration-300 group text-left p-6" data-id="element-670">
                {/* Header Area */}
                <div className="flex items-center gap-4 mb-8" data-id="element-671">
                  <div className="w-12 h-12 rounded-full bg-gray-100 p-2 flex items-center justify-center flex-shrink-0" data-id="element-672">
                    <img src={client.logo} alt={client.name} className="max-w-full max-h-full object-contain" data-id="element-673" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-674">
                    <div className="text-lg text-foreground truncate font-normal group-hover:text-[#0187c3] transition-colors" data-id="element-675">
                      {client.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-normal" data-id="element-676">
                      Ultimo agg:{' '}
                      {new Date(client.lastUpdate).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                    </div>
                  </div>
                </div>

                {/* KPIs Area */}
                <div className="space-y-6" data-id="element-677">
                  <div className="grid grid-cols-2 gap-4" data-id="element-678">
                    <div data-id="element-679">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-normal" data-id="element-680">
                        Articoli / mese
                      </div>
                      <div className="text-xl text-foreground font-light" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-681">
                        {client.articlesThisMonth}
                      </div>
                    </div>
                    <div data-id="element-682">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-normal" data-id="element-683">
                        Score medio
                      </div>
                      <div className="text-xl text-[#0187c3] font-light" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-684">
                        {client.overallScore.toFixed(1)}
                      </div>
                    </div>
                  </div>

                  {/* Sentiment Area */}
                  <div data-id="element-685">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-normal" data-id="element-686">
                      Sentiment
                    </div>
                    <div className="flex items-center gap-4" data-id="element-687">
                      <div className="flex items-center gap-1.5" data-id="element-688">
                        <span className="w-3 h-3 rounded-full bg-[#0187c3]" data-id="element-689" />
                        <span className="text-muted-foreground text-sm" data-id="element-690">
                          {sentiment.positive}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-691">
                        <span className="w-3 h-3 rounded-full bg-[#64B5F6]" data-id="element-692" />
                        <span className="text-muted-foreground text-sm" data-id="element-693">
                          {sentiment.neutral}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-694">
                        <span className="w-3 h-3 rounded-full bg-[#1A3A5C]" data-id="element-695" />
                        <span className="text-muted-foreground text-sm" data-id="element-696">
                          {sentiment.negative}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.button>;
        })}
        </motion.div>
      </div>
    </div>;
}